/**
 * Documents Tab - Table-based view matching Entries tab design
 * Displays document clusters (grouped by entry number) in a scrollable table
 */
import { FC, useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  CircularProgress,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import {
  Close,
  Search as SearchIcon,
  Upload as UploadIcon,
  PlaylistAddCheck as AuditAllIcon,
} from "@mui/icons-material";

import { useAppSelector } from "@/store";
import {
  useGetCustomsClustersQuery,
  useGetCustomsDocumentsQuery,
  useDeleteCustomsDocumentMutation,
  useDeleteCustomsClusterMutation,
  useLazyGetCustomsDocumentQuery,
  useLazyGetClusterAuditQuery,
  useQueueClusterAuditMutation,
  useQueueMultipleAuditsMutation,
} from "@/services";
import {
  useDebounce,
  useSnackbar,
  useDocumentProcessingPolling,
} from "@/hooks";
import { useEffectiveClientScope } from "@/hooks/useEffectiveClientScope";
import { DocumentAccessLevel, roles } from "@/roles";
import AuditDetailModal from "@/components/audit-detail-modal";
import UploadCustomsDocuments from "../../documents/upload-customs-documents";
import { tableCompactSx } from "@/components/table/table-styles";
import Pagination from "@/components/paginations/pagination";
import { ClusterRow } from "./ClusterRow";
import { UnclusteredRow } from "./UnclusteredRow";
import { DeleteClusterConfirmDialog } from "./DeleteClusterConfirmDialog";
import { DocumentDetailDialog } from "./document-detail-dialog";
import { tokens } from "@/tokens";
import { tariff_code_placeholder } from "@/constants";

const INITIAL_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

// Table column definitions
const COLUMN_COUNT = 8;
const columns = [
  { key: "expand", label: "", width: "48px", align: "center" as const },
  { key: "entryNumber", label: "Entry #", align: "left" as const },
  { key: "identifiers", label: "Identifiers", align: "left" as const },
  { key: "documentCount", label: "Documents", align: "center" as const },
  { key: "documentTypes", label: "Document Types", align: "left" as const },
  { key: "auditStatus", label: "Status", align: "center" as const },
  { key: "audit", label: "Audit", align: "center" as const },
  { key: "actions", label: "", align: "center" as const, width: "48px" },
];

const DocumentsTab: FC = () => {
  // Pagination state
  const [page, setPage] = useState(INITIAL_PAGE);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

  // Expansion state
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(
    new Set()
  );
  const [showUnclustered, setShowUnclustered] = useState(false);

  // Search state
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Loading states
  const [loadingStates, setLoadingStates] = useState<{
    delete: string[];
    download: string[];
    audit: string[];
    deleteCluster: string[];
  }>({
    delete: [],
    download: [],
    audit: [],
    deleteCluster: [],
  });

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [auditResults, setAuditResults] = useState<
    Record<string, ClusterAudit>
  >({});
  const [selectedAudit, setSelectedAudit] = useState<ClusterAudit | null>(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [clusterToDelete, setClusterToDelete] =
    useState<CustomsCluster | null>(null);
  const [showDeleteClusterModal, setShowDeleteClusterModal] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<CustomsDocument | null>(null);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);

  // Hooks
  const { show } = useSnackbar();
  // selectedClient/selectedTenant are kept around for the audit-results-clear
  // effect and the upload modal; scope decisions all flow through the shared
  // useEffectiveClientScope hook so we don't fork the rule.
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  const {
    clientId: effectiveClientId,
    tenantId: effectiveTenantId,
    isTDAdmin,
    shouldRequireClientSelection,
  } = useEffectiveClientScope();
  const shouldSkip = shouldRequireClientSelection;

  const [deleteCustomsDocument] = useDeleteCustomsDocumentMutation();
  const [deleteCustomsCluster] = useDeleteCustomsClusterMutation();
  const [getCustomsDocument] = useLazyGetCustomsDocumentQuery();
  const [getClusterAudit] = useLazyGetClusterAuditQuery();
  const [queueClusterAudit] = useQueueClusterAuditMutation();
  const [queueMultipleAudits, { isLoading: isAuditingAll }] = useQueueMultipleAuditsMutation();

  const canUploadDocs = useMemo(
    () =>
      user?.userRole !== roles.SPCLIENT ||
      user?.clientPermission?.documents === DocumentAccessLevel.Admin,
    [user]
  );

  // Fetch clusters - fetch ALL clusters, filter on frontend
  const {
    data: clustersData,
    isLoading: clustersLoading,
    isFetching: clustersFetching,
    refetch: refetchClusters,
  } = useGetCustomsClustersQuery(
    {
      pageSize: 1000,
      pageNo: 1,
      clientId: effectiveClientId,
      ...(isTDAdmin && effectiveTenantId && { tenantId: effectiveTenantId }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  // Fetch all documents
  const {
    data: documentsData,
    isLoading: documentsLoading,
    isFetching: documentsFetching,
    refetch: refetchDocuments,
  } = useGetCustomsDocumentsQuery(
    {
      pageSize: 500,
      pageNo: 1,
      clientId: effectiveClientId,
      ...(isTDAdmin && effectiveTenantId && { tenantId: effectiveTenantId }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  const isRefetching =
    (clustersFetching && !clustersLoading) ||
    (documentsFetching && !documentsLoading);

  // Poll for document processing status
  const { isPolling, processingCounts } = useDocumentProcessingPolling({
    documents: documentsData?.data,
    onRefetch: () => {
      refetchDocuments();
      refetchClusters();
    },
    pollingInterval: 3000,
    enabled: !shouldSkip,
  });

  // Build cluster ID set
  const clusterIdSet = useMemo(() => {
    return new Set(clustersData?.data?.map((c) => c._id) || []);
  }, [clustersData?.data]);

  // Group documents by cluster
  const { clusteredDocs, unclusteredDocs } = useMemo(() => {
    const allDocs = documentsData?.data || [];
    const clusters = clustersData?.data || [];

    const clustered: Record<string, CustomsDocument[]> = {};
    const unclustered: CustomsDocument[] = [];

    clusters.forEach((cluster) => {
      clustered[cluster._id] = [];
    });

    allDocs.forEach((doc) => {
      if (doc.clusterId) {
        if (clustered[doc.clusterId]) {
          clustered[doc.clusterId].push(doc);
        } else if (clusterIdSet.has(doc.clusterId)) {
          clustered[doc.clusterId] = [doc];
        } else {
          unclustered.push(doc);
        }
      } else {
        unclustered.push(doc);
      }
    });

    return { clusteredDocs: clustered, unclusteredDocs: unclustered };
  }, [documentsData, clustersData, clusterIdSet]);

  // Get all clusters (filtered by search on frontend)
  const allClusters = useMemo(() => {
    const clusters = clustersData?.data || [];
    if (!searchText.trim()) return clusters;

    const searchLower = searchText.toLowerCase().trim();
    return clusters.filter((cluster) => {
      // Check entry numbers
      if (cluster.entryNumbers?.some((en) => en.toLowerCase().includes(searchLower))) {
        return true;
      }
      // Check cluster keys (BL, Container, PO, Invoice)
      if (cluster.clusterKeys?.some((key) => key.value?.toLowerCase().includes(searchLower))) {
        return true;
      }
      return false;
    });
  }, [clustersData?.data, searchText]);

  // Paginate clusters
  const paginatedClusters = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return allClusters.slice(startIndex, endIndex);
  }, [allClusters, page, limit]);

  const totalClusters = allClusters.length;

  // Filter unclustered docs when searching (by filename)
  const filteredUnclusteredDocs = useMemo(() => {
    if (!searchText.trim()) return unclusteredDocs;
    const searchLower = searchText.toLowerCase().trim();
    return unclusteredDocs.filter((doc) =>
      doc.filename?.toLowerCase().includes(searchLower)
    );
  }, [unclusteredDocs, searchText]);

  // Document statistics
  const stats = useMemo(() => {
    const total = Object.values(clusteredDocs).reduce(
      (sum, docs) => sum + docs.length,
      0
    );
    return {
      totalDocuments: total + unclusteredDocs.length,
      totalClusters: allClusters.length,
      unclustered: filteredUnclusteredDocs.length,
    };
  }, [clusteredDocs, unclusteredDocs, allClusters, filteredUnclusteredDocs]);

  // Handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setExpandedClusters(new Set());
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(INITIAL_PAGE);
    setExpandedClusters(new Set());
  };

  const toggleCluster = (clusterId: string) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) {
        next.delete(clusterId);
      } else {
        next.add(clusterId);
      }
      return next;
    });
  };

  const handleDownload = async (doc: CustomsDocument) => {
    setLoadingStates((prev) => ({
      ...prev,
      download: [...prev.download, doc._id],
    }));
    try {
      const result = await getCustomsDocument({
        docId: doc._id,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();
      if (result?.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
      }
    } catch (error) {
      show("Download Failed", (error as Error).message, "error");
    }
    setLoadingStates((prev) => ({
      ...prev,
      download: prev.download.filter((id) => id !== doc._id),
    }));
  };

  const handleDelete = async (doc: CustomsDocument) => {
    setLoadingStates((prev) => ({
      ...prev,
      delete: [...prev.delete, doc._id],
    }));
    try {
      const isDeleted = await deleteCustomsDocument({
        docId: doc._id,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();
      if (isDeleted) {
        show(
          "Successfully deleted!",
          `Document "${doc.filename}" deleted successfully!`,
          "success"
        );
      }
    } catch (error) {
      show("Delete Failed", (error as Error).message, "error");
    }
    setLoadingStates((prev) => ({
      ...prev,
      delete: prev.delete.filter((id) => id !== doc._id),
    }));
  };

  const handleAuditAll = async () => {
    const clusters = clustersData?.data || [];
    // Only queue clusters that aren't already queued/processing
    const clusterIds = clusters
      .filter(c => c.auditQueueStatus !== "QUEUED" && c.auditQueueStatus !== "PROCESSING")
      .map(c => c._id);

    if (clusterIds.length === 0) {
      show("No Clusters", "All clusters are already queued or processing.", "info");
      return;
    }

    try {
      const result = await queueMultipleAudits({
        clusterIds,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();

      show(
        "Audits Queued",
        `${result.queued} audit${result.queued !== 1 ? "s" : ""} queued.${result.alreadyQueued > 0 ? ` ${result.alreadyQueued} already in queue.` : ""}`,
        "success"
      );
      refetchClusters();
    } catch (error) {
      show("Queue Failed", (error as Error).message, "error");
    }
  };

  const handleRunAudit = async (clusterId: string) => {
    setLoadingStates((prev) => ({
      ...prev,
      audit: [...prev.audit, clusterId],
    }));
    try {
      const result = await queueClusterAudit({
        clusterId,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();
      if (result) {
        if (result.alreadyQueued) {
          show(
            "Already Queued",
            "This cluster is already in the audit queue.",
            "info"
          );
        } else {
          const position = result.queueItem.position;
          show(
            "Audit Queued",
            position
              ? `Audit queued at position #${position}. It will start automatically.`
              : "Audit queued. It will start automatically.",
            "success"
          );
        }
        refetchClusters();
      }
    } catch (error) {
      show("Queue Failed", (error as Error).message, "error");
    }
    setLoadingStates((prev) => ({
      ...prev,
      audit: prev.audit.filter((id) => id !== clusterId),
    }));
  };

  const handleViewAudit = async (
    clusterId: string,
    existingAudit?: ClusterAudit
  ) => {
    if (existingAudit) {
      setSelectedAudit(existingAudit);
      setShowAuditModal(true);
      return;
    }

    try {
      const result = await getClusterAudit({
        clusterId,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();
      if (result) {
        setSelectedAudit(result);
        setShowAuditModal(true);
      }
    } catch (error) {
      show("Error", "Failed to load audit details", "error");
    }
  };

  const handleDeleteClusterClick = (cluster: CustomsCluster) => {
    setClusterToDelete(cluster);
    setShowDeleteClusterModal(true);
  };

  const handleDeleteClusterConfirm = async () => {
    if (!clusterToDelete) return;

    const clusterIdToDelete = clusterToDelete._id;
    setLoadingStates((prev) => ({
      ...prev,
      deleteCluster: [...prev.deleteCluster, clusterIdToDelete],
    }));
    setShowDeleteClusterModal(false);

    try {
      const result = await deleteCustomsCluster({
        clusterId: clusterIdToDelete,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();

      if (result.success) {
        show(
          "Folder Deleted",
          `Successfully deleted folder and ${result.deletedDocuments} document${result.deletedDocuments !== 1 ? "s" : ""}.`,
          "success"
        );
        setAuditResults((prev) => {
          const next = { ...prev };
          delete next[clusterIdToDelete];
          return next;
        });
      }
    } catch (error) {
      show("Delete Failed", (error as Error).message, "error");
    }

    setLoadingStates((prev) => ({
      ...prev,
      deleteCluster: prev.deleteCluster.filter((id) => id !== clusterIdToDelete),
    }));
    setClusterToDelete(null);
  };

  const handleDocumentClick = (doc: CustomsDocument) => {
    setSelectedDocument(doc);
    setShowDocumentDetail(true);
  };

  // Clear audit results when tenant/client changes
  useEffect(() => {
    setAuditResults({});
  }, [selectedClient?._id, selectedTenant?._id]);

  // Poll clusters when there are queued/processing audits
  const hasActiveQueueItems = useMemo(() => {
    const clusterList = clustersData?.data || [];
    return clusterList.some(
      (c) =>
        c.auditQueueStatus === "QUEUED" || c.auditQueueStatus === "PROCESSING"
    );
  }, [clustersData?.data]);

  useEffect(() => {
    if (!hasActiveQueueItems || shouldSkip) return;

    const pollInterval = setInterval(() => {
      refetchClusters();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [hasActiveQueueItems, shouldSkip, refetchClusters]);

  // Debounced search
  const debouncedSearch = useDebounce<string>((value) => {
    setSearchText(value);
    setPage(INITIAL_PAGE);
  }, 500);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearchText("");
    setPage(INITIAL_PAGE);
  };

  const isLoading = clustersLoading || documentsLoading;

  // Render skeleton rows
  const renderSkeletonRows = () => {
    return Array.from({ length: limit }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {columns.map((col) => (
          <TableCell key={col.key}>
            <Skeleton variant="rectangular" height={24} />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  if (shouldSkip) {
    return (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography sx={{ fontSize: tokens.fontSize.xl, fontWeight: tokens.fontWeight.semibold, color: tokens.text.secondary }}>
          Please select a client to view documents
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      {/* Stats count */}
      <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, px: 3, pt: 2, pb: 0.5, gap: 1 }}>
        <Typography sx={{ fontSize: tokens.fontSize.md, color: tokens.text.secondary, whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{stats.totalClusters}</Box>
          {' '}{stats.totalClusters === 1 ? 'folder' : 'folders'}
          {' · '}
          <Box component="span" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{stats.totalDocuments}</Box>
          {' '}{stats.totalDocuments === 1 ? 'document' : 'documents'}
          {stats.unclustered > 0 && (
            <>
              {' · '}
              <Box component="span" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{stats.unclustered}</Box>
              {' unclustered'}
            </>
          )}
        </Typography>
        {(isLoading || isRefetching) && (
          <CircularProgress size={14} sx={{ color: tokens.text.muted }} />
        )}
      </Stack>

      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flexShrink: 0, px: 3, pt: 1, pb: 2, gap: 1.5 }}
      >
        {/* Search */}
        <OutlinedInput
          size="small"
          placeholder="Search Entry, BL, Container, PO..."
          value={searchInput}
          onChange={handleSearchChange}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
            </InputAdornment>
          }
          endAdornment={searchInput ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleSearchClear}>
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined}
          sx={{
            backgroundColor: tokens.bg.surface,
            borderRadius: tokens.radius.md,
            minWidth: 280,
            height: 36,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border.default,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.text.disabled,
            },
            '& .MuiOutlinedInput-input': {
              py: 0.75,
              fontSize: tokens.fontSize.sm2,
            },
          }}
        />

        {/* Audit All */}
        <Button
          variant="outlined"
          startIcon={isAuditingAll ? <CircularProgress size={14} /> : <AuditAllIcon sx={{ fontSize: 18 }} />}
          onClick={handleAuditAll}
          disabled={isAuditingAll || !clustersData?.data?.length}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            bgcolor: tokens.bg.surface,
            '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 36,
            px: 2,
            '&.Mui-disabled': { borderColor: tokens.bg.muted, color: tokens.text.disabled },
          }}
        >
          Audit All
        </Button>

        {/* Upload - pushed right */}
        {canUploadDocs && (
          <Button
            variant="contained"
            startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
            onClick={() => setShowUploadModal(true)}
            sx={{
              ml: 'auto',
              bgcolor: tokens.brand.primary,
              '&:hover': { bgcolor: tokens.brand.primaryHover },
              borderRadius: tokens.radius.md,
              fontWeight: tokens.fontWeight.bold,
              fontSize: tokens.fontSize.md,
              height: 36,
              px: 2,
              boxShadow: 'none',
            }}
          >
            Upload
          </Button>
        )}
      </Stack>

      {/* Processing Banner */}
      {isPolling && processingCounts.processing > 0 && (
        <Box
          sx={{
            mx: 3,
            mb: 1.5,
            px: 2,
            py: 1,
            bgcolor: `${tokens.status.info}08`,
            border: `1px solid ${tokens.status.info}30`,
            borderRadius: tokens.radius.md,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <CircularProgress size={14} sx={{ color: tokens.status.info }} />
          <Typography sx={{ color: tokens.status.info, fontSize: tokens.fontSize.base }}>
            Processing {processingCounts.processing} document
            {processingCounts.processing !== 1 ? "s" : ""}...
            {processingCounts.ready > 0 && (
              <Box component="span" sx={{ ml: 1, color: tokens.status.success }}>
                {processingCounts.ready} ready
              </Box>
            )}
            {processingCounts.failed > 0 && (
              <Box component="span" sx={{ ml: 1, color: tokens.status.danger }}>
                {processingCounts.failed} failed
              </Box>
            )}
          </Typography>
        </Box>
      )}

      {/* Table */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            mx: 3,
            mb: 2,
            borderRadius: tokens.radius.lg,
            border: `1px solid ${tokens.border.default}`,
            bgcolor: tokens.bg.surface,
            overflow: "hidden",
            ...tableCompactSx,
          }}
        >
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{
                        ...(col.width && { width: col.width }),
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  renderSkeletonRows()
                ) : paginatedClusters.length === 0 &&
                  unclusteredDocs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      sx={{ border: 0 }}
                    >
                      <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 6 }}>
                        <img src={tariff_code_placeholder} alt="No documents" style={{ width: 120, height: 120, opacity: 0.6 }} />
                        <Typography variant="h6" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                          No Documents Yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: tokens.text.secondary, maxWidth: 420, textAlign: "center", lineHeight: 1.6 }}>
                          Upload your customs documents to organize entries, run compliance audits, and keep everything in one place.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
                          onClick={() => setShowUploadModal(true)}
                          sx={{
                            mt: 1,
                            bgcolor: tokens.brand.primary,
                            fontWeight: tokens.fontWeight.semibold,
                            fontSize: tokens.fontSize.sm2,
                            borderRadius: tokens.radius.md,
                            textTransform: "none",
                            px: 2.5,
                            boxShadow: "none",
                            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
                          }}
                        >
                          Upload Documents
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {paginatedClusters.map((cluster) => {
                      const docs = clusteredDocs[cluster._id] || [];

                      return (
                        <ClusterRow
                          key={cluster._id}
                          cluster={cluster}
                          documents={docs}
                          isExpanded={expandedClusters.has(cluster._id)}
                          onToggle={() => toggleCluster(cluster._id)}
                          onDownload={handleDownload}
                          onDelete={handleDelete}
                          onDeleteCluster={handleDeleteClusterClick}
                          loadingStates={loadingStates}
                          onRunAudit={handleRunAudit}
                          onViewAudit={handleViewAudit}
                          audit={auditResults[cluster._id] || null}
                          onDocumentClick={handleDocumentClick}
                        />
                      );
                    })}

                    {/* Show unclustered row on first page only */}
                    {page === 1 && (
                      <UnclusteredRow
                        documents={filteredUnclusteredDocs}
                        isExpanded={showUnclustered}
                        onToggle={() => setShowUnclustered((prev) => !prev)}
                        onDownload={handleDownload}
                        onDelete={handleDelete}
                        loadingStates={loadingStates}
                        onDocumentClick={handleDocumentClick}
                      />
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination — inside card */}
          {totalClusters > 0 && (
            <Pagination
              page={page}
              limit={limit}
              total={totalClusters}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </Stack>
      </Box>

      {/* Upload Modal */}
      <UploadCustomsDocuments
        show={showUploadModal}
        setShow={setShowUploadModal}
        onUploadComplete={() => {
          refetchClusters();
          refetchDocuments();
        }}
        tenantId={isTDAdmin && selectedTenant ? selectedTenant._id : undefined}
        clientId={effectiveClientId}
      />

      {/* Audit Detail Modal */}
      <AuditDetailModal
        open={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        audit={selectedAudit}
      />

      {/* Delete Cluster Confirmation Dialog */}
      <DeleteClusterConfirmDialog
        open={showDeleteClusterModal}
        onClose={() => {
          setShowDeleteClusterModal(false);
          setClusterToDelete(null);
        }}
        onConfirm={handleDeleteClusterConfirm}
        cluster={clusterToDelete}
        documentCount={
          clusterToDelete
            ? clusteredDocs[clusterToDelete._id]?.length ||
              clusterToDelete.documentCount
            : 0
        }
        hasAuditResults={
          clusterToDelete ? !!auditResults[clusterToDelete._id] : false
        }
      />

      {/* Document Detail Dialog */}
      <DocumentDetailDialog
        open={showDocumentDetail}
        onClose={() => {
          setShowDocumentDetail(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        tenantId={isTDAdmin && selectedTenant ? selectedTenant._id : undefined}
      />
    </Stack>
  );
};

export default DocumentsTab;
